<template>

  <!-- Modal -->
  <div v-if="isModalOpen" class="modal-overlay" @click="closeModal">
    <div class="modal-content card" @click.stop style="max-width: 800px;">
      <button class="btn-close text-dark" @click="closeModal"></button>

      <div v-if="!showErrorDetails">

        <div class="row">
          <!-- Primera columna: Icono grande -->
          <div class="col-lg-2 col-md-12 d-flex justify-content-center align-items-center">
            <i class="fas fa-upload text-dark icon-large"></i>
          </div>

          <!-- Segunda columna: Contenido -->
          <div class="col-lg-10 col-md-12 pe-2 pe-lg-4">
            <div class="py-2 px-0 card-header">
              <!-- Title Section -->
              <div class="d-flex title justify-content-between pb-2">
                <h4 class="mb-2">Carga Masiva</h4>
              </div>

              <!-- Step 1: Instruction -->
              <div class="instruction-step mb-3">
                <p class="d-flex justify-content-left text-sm fw-bold mb-1">Paso 1: Descargue la plantilla</p>       
                <div class="d-flex justify-content-center mt-2">
                  <button class="btn btn-sm btn-outline-success mb-0 me-2 me-md-4" @click="downloadTemplateCSV()"
                    :disabled="isLoading">
                    <i class="fas fa-download me-1"></i>
                    Descargar Plantilla (.csv)
                  </button>
                </div>
              </div>

              <!-- Step 2: Upload -->
              <div class="instruction-step">
                <p class="d-flex justify-content-left text-sm fw-bold mb-1">Paso 2: Cargue su archivo</p>
                <p class="d-flex justify-content-left text-sm mb-1">
                  <span class="text-danger me-2">* </span> Solo se aceptarán archivos basados en la plantilla descargada.
                </p>
                <p class="d-flex justify-content-left text-sm mb-0">
                  <span class="text-danger me-2">* </span> Mantenga la estructura de columnas y formato CSV sin modificaciones.
                </p>
              </div>
            </div>

            <!-- Área de arrastrar y soltar -->
            <div class="drop-area" :class="{ 'drag-over': dragOver }" @dragover.prevent="dragOver = true"
              @dragleave="dragOver = false" @drop="handleDrop" @click="triggerFileInput"
              :style="{ pointerEvents: isLoading ? 'none' : 'auto' }">
              <p class="mb-0" v-if="!selectedFile && !isLoading">Arrastre y suelte su archivo aquí</p>
              <p class="mb-0" v-else-if="selectedFile && !isLoading">Archivo seleccionado: {{ selectedFile.name }}</p>

              <!-- Mostrar el spinner cuando está cargando -->
              <div v-if="isLoading" class="spinner-container">
                <div class="spinner-border text-text-dark" role="status">
                  <span class="visually-hidden">Cargando...</span>
                </div>
              </div>
            </div>

            <!-- Ocultamos el input de archivo ya que se activará al hacer clic en la zona de arrastre -->
            <input type="file" ref="fileInput" @change="handleFileChange" style="display: none;" />
            <div class="d-flex justify-content-end align-items-center">
              <p v-if="errorMessage" class="text-danger mb-4 text-xs pe-2 pe-md-7">{{ errorMessage }}</p>
              <p class="mb-4 text-xs">
                *Formato admitido: <strong>.csv .xlsx</strong>
              </p>
            </div>
            <!-- Botones de acción -->
            <div class="d-flex mt-4 justify-content-end align-items-center">

              <button class="btn d-flex mb-0 me-2 me-md-4" @click="uploadExcelFile" :disabled="isLoading">
                <i class="text-sm fas fa-upload text-dark pe-2"></i>
                <span v-if="isLoading">Cargando...</span>
                <span v-else>Cargar</span>
              </button>
              <button class="btn d-flex mb-0 " @click="closeModal">
                <i class="text-sm fas fa-times text-secondary pe-2"></i>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Details View -->
      <div v-else>
        <div class="card-header p-3 d-flex justify-content-start me-5 align-items-center">
          <button class="btn btn-sm btn-outline-secondary" @click="showErrorDetails = false">
            <i class="fas fa-arrow-left me-1 mb-0"></i> Volver
          </button>
          <h5 class="mb-0 ms-3">Detalles de Errores (Carga #{{ currentCargaId }})</h5>
        </div>

        <div class="card-body">
          <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
            <table class="table align-items-start mb-0">
              <thead>
                <tr>
                  <th class="ps-1">Línea</th>
                  <th class="ps-1">Cédula</th>
                  <th class="ps-1">Mensaje de Error</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(error, idx) in errorDetails" :key="idx">
                  <td class="text-xs">{{ error.linea }}</td>
                  <td class="text-xs">{{ error.cedula || 'N/A' }}</td>
                  <td class="text-xs text-danger">{{ error.mensaje }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-end mt-3">
            <button class="btn btn-sm btn-outline-success" @click="downloadErrorCSV(currentCargaId)">
              <i class="fas fa-download me-1 mb-0"></i> Descargar CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import { ref } from "vue";
import axios from "axios";
import Swal from "sweetalert2"; // Import SweetAlert2
import { BACK_END_URL } from '@/utilities/config';

const isModalOpen = ref(false);

const openModal = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};

const selectedFile = ref(null);
const dragOver = ref(false);
const isLoading = ref(false);
const errorMessage = ref("");

const showErrorDetails = ref(false);
const errorDetails = ref([]);
const currentCargaId = ref(null);

defineExpose({
  openModal,
  closeModal
});

const handleDrop = (event) => {
  event.preventDefault();
  dragOver.value = false;
  const files = event.dataTransfer.files;
  if (files.length > 0) {
    selectedFile.value = files[0];
  }
};

const triggerFileInput = () => {
  const fileInput = document.querySelector('input[type="file"]');
  fileInput?.click();
};

const handleFileChange = (event) => {
  selectedFile.value = event.target.files[0];
};

const uploadExcelFile = async () => {
  if (!selectedFile.value) {
    errorMessage.value = "Por favor, seleccione un archivo antes de subirlo.";
    return;
  }

  const allowedExtensions = [".csv", ".xlsx"];
  const fileExtension = selectedFile.value.name.slice(selectedFile.value.name.lastIndexOf(".")).toLowerCase();
  if (!allowedExtensions.includes(fileExtension)) {
    errorMessage.value = "Solo se permiten archivos .csv y .xlsx.";
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";

  const formData = new FormData();
  formData.append('file', selectedFile.value);

  try {
    const token = sessionStorage.getItem("token");
    const response = await axios.post(`${BACK_END_URL}/analytics/EXCELupload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      }
    });

    await Swal.fire({
      title: "¡Archivo cargado exitosamente!",
      html: `
      <p>Se cargó el archivo correctamente. Ahora puedes revisar los registros con errores (si los hay) en el historial de cargas.</p>
        <ul class="text-start">
          <li>Registros actualizados: ${response.data.actualizados}</li>
          <li>Errores encontrados: ${response.data.errores}</li>
        </ul>
      `,
      icon: "success",
      confirmButtonText: "OK",
    });

    if (response.data.errores > 0 && response.data.carga_id) {
      currentCargaId.value = response.data.carga_id;
      await fetchErrorDetails(response.data.carga_id);
      showErrorDetails.value = true;
    } else {
      closeModal();
    }

  } catch (error) {
    console.error("Error uploading Excel file:", error);

    let errorMsg = "Error al procesar el archivo Excel";
    if (error.response) {
      if (error.response.data.message) {
        errorMsg = error.response.data.message;
      } else if (error.response.status === 413) {
        errorMsg = "El archivo es demasiado grande";
      }
    }

    Swal.fire({
      title: "Error",
      text: errorMsg,
      icon: "error"
    });
  } finally {
    isLoading.value = false;
  }
};

const downloadTemplateCSV = async () => {
  try {
    const token = sessionStorage.getItem("token");
    const response = await axios.get(`${BACK_END_URL}/analytics/descargar-template`, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading CSV template:", error);
    Swal.fire({
      title: "Error",
      text: "No se pudo descargar la plantilla CSV",
      icon: "error"
    });
  }
};

const fetchErrorDetails = async (cargaId) => {
  try {
    const token = sessionStorage.getItem("token");
    const response = await axios.get(`${BACK_END_URL}/carga-errores`, {
      params: { carga_id: cargaId },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    errorDetails.value = response.data;
  } catch (err) {
    Swal.fire({
      title: "Error",
      text: "No se pudieron cargar los detalles de errores",
      icon: "error"
    });
    console.error("Error fetching error details:", err);
  }
};

const downloadErrorCSV = async (cargaId) => {
  try {
    const token = sessionStorage.getItem("token");
    const response = await axios.get(`${BACK_END_URL}/carga-errores/csv`, {
      params: { carga_id: cargaId },
      headers: {
        Authorization: `Bearer ${token}`
      },
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `errores_carga_${cargaId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (err) {
    Swal.fire({
      title: "Error",
      text: "No se pudo descargar el archivo CSV",
      icon: "error"
    });
  }
};
</script>

<style scoped>
/* Fondo del modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  /* Ensures it covers the whole screen */
  width: 100vw;
  height: 100vh;
  background: rgba(0, 24, 6, 0.384);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  /* Ensure it appears on top */
}


/* Contenedor del modal */
.modal-content {
  background: white;
  padding: 20px;
  border-radius: 15px;
  width: 45%;
  text-align: center;
  display: flex;
  flex-direction: column;
  overflow: hidden;

}


/* Icono grande */
.icon-large {
  font-size: 4rem;
  color: #2e2f30;
}

/* Área de arrastrar y soltar */
.drop-area {
  border: 2px dashed #ccc;
  padding: 40px;
  margin: 20px 0;
  cursor: pointer;
  border-radius: 10px;
  position: relative;
  /* Needed for positioning the spinner */
}

.drop-area:hover {
  border-color: #2dce89;
  background: #e7eaed;
}

.drag-over {
  border-color: #2dce89;
  background: #e7eaed;
}

@media (max-width: 1200px) {
  .modal-overlay {
    width: 100%;
  }

  .modal-content {
    width: 400px;
    max-width: 400px;
    padding: 15px;
    margin: 5%;
    max-height: 85vh;
    padding-bottom: 25px;
  }

  .modal-content h6,
  .modal-content p {
    font-size: 12px !important;
  }

  .modal-content .btn {
    font-size: 12px;
    padding: 8px 12px;
    margin-top: 0%;
  }

  .modal-content .d-flex {
    align-items: flex-start;
  }

  .no-wrap-mobile {
    flex-wrap: nowrap !important;
  }

  h4 {
    justify-content: center !important;
    font-size: 1rem !important;
  }

  .icon-large {
    font-size: 2rem !important;
  }

  .fixed-plugin .fixed-plugin-button {
    right: 70% !important;
  }

  .fixed-plugin .fixed-plugin-button {
    bottom: 30px;
    right: 95% !important;
    z-index: 1050;
  }

}


/* Spinner en la zona de arrastre */
.spinner-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Botones */
.modal-buttons {
  display: flex;
  justify-content: space-around;
  margin-top: 10px;
}

.modal-buttons button {
  padding: 8px 12px;
  border: none;
  cursor: pointer;
}

.modal-buttons button:first-child {
  background: #28a745;
  color: white;
}

.modal-buttons button:last-child {
  background: #dc3545;
  color: white;
}
</style>
